import { EnvironmentProviders, makeEnvironmentProviders, inject, provideAppInitializer } from '@angular/core';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  PublicClientApplication,
} from '@azure/msal-browser';
import { AuthService } from 'shared/core';
import { KF_AUTH_CONFIG, KfAuthConfig, KfAuthOptions, resolveApiScope } from './kf-auth.config';
import { KfInterceptor } from './kf.interceptor';

/**
 * One call wires the whole KanzenFlow auth layer into a standalone
 * application: MSAL instance, MsalService, HttpClient with the tenant/bearer
 * interceptor, and the async MSAL initialization the host performs through its
 * APP_INITIALIZER.
 *
 * Every vertical application calls this with its own environment values, so no
 * vertical re-implements auth and none of them imports another app's
 * environment file.
 *
 * The MSAL instance is created exactly once here, which is what keeps a single
 * token cache per application.
 */
export function provideKanzenflowAuth(options: KfAuthOptions): EnvironmentProviders {
  const config: KfAuthConfig = { ...options, apiScope: resolveApiScope(options.clientId, options.apiScope) };
  return makeEnvironmentProviders([
    { provide: KF_AUTH_CONFIG, useValue: config },

    {
      provide: MSAL_INSTANCE,
      useFactory: (): IPublicClientApplication =>
        new PublicClientApplication({
          auth: {
            clientId: config.clientId,
            authority: config.authority,
            redirectUri: config.redirectUri,
            postLogoutRedirectUri: config.postLogoutRedirectUri,
          },
          cache: { cacheLocation: BrowserCacheLocation.LocalStorage },
        }),
    },

    {
      provide: MSAL_GUARD_CONFIG,
      useValue: {
        interactionType: InteractionType.Redirect,
        authRequest: { scopes: [config.apiScope] },
      } as MsalGuardConfiguration,
    },

    MsalService,
    MsalBroadcastService,
    MsalGuard,

    provideHttpClient(withInterceptorsFromDi()),
    { provide: HTTP_INTERCEPTORS, useClass: KfInterceptor, multi: true },

    // MSAL v5 requires initialize() to resolve before any token call, and
    // handleRedirectPromise() must run on every page load so the return leg of
    // a redirect sign-in is processed. The host application does this by
    // bootstrapping MsalRedirectComponent; a standalone application has no
    // second bootstrap, so the library owns it here. Without this the browser
    // comes back from Microsoft with a code in the fragment and nothing
    // consumes it.
    provideAppInitializer(async () => {
      const instance = inject(MsalService).instance;
      const auth = inject(AuthService);
      await instance.initialize();

      const redirectResult = await instance.handleRedirectPromise();
      if (redirectResult?.account) {
        instance.setActiveAccount(redirectResult.account);
        // The sign-in is complete here and nowhere else. Rexine used to notice
        // it from an MSAL broadcast event subscribed by its sign-in page, which
        // only worked because that page happened to be mounted; the redirect
        // lands on whatever route the application registered, so the page is
        // the wrong place to watch from. Recording it here means the session
        // exists before the first guard runs.
        auth.recordSignIn(redirectResult);
        return;
      }

      // Returning user with a cached account and no redirect in flight.
      const [existing] = instance.getAllAccounts();
      if (existing && !instance.getActiveAccount()) {
        instance.setActiveAccount(existing);
      }
    }),
  ]);
}
